import { StorageProvider, User, StorageConfig, SyncStatus } from './storageTypes';
import { LocalStorageProvider } from './localStorageProvider';

interface SessionData {
  id: number;
  date: string;
  wordCount: number;
  charCount: number;
  sessionDuration?: number;
  deletedChars?: number;
  deletedWords?: number;
  numTimesDeleto?: number;
  sessionEndedEarly?: boolean;
  sessionCompleted?: boolean;
}

export class HybridStorageService implements StorageProvider {
  private localStorage: LocalStorageProvider;
  private supabaseProvider: StorageProvider | null = null;
  private config: StorageConfig;
  private syncStatus: SyncStatus;

  constructor(
    private user: User | null = null,
    config?: Partial<StorageConfig>
  ) {
    this.localStorage = new LocalStorageProvider();
    this.config = {
      enableSupabase: false, // Will be enabled when Supabase is implemented
      enableLocalBackup: true,
      syncOnConnect: true,
      ...config
    };
    
    this.syncStatus = {
      isOnline: navigator.onLine,
      lastSync: null,
      hasPendingChanges: false,
      syncInProgress: false
    };

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.syncStatus.isOnline = true;
      this.syncPendingChanges();
    });
    
    window.addEventListener('offline', () => {
      this.syncStatus.isOnline = false;
    });
  }

  // Update user context (called when auth state changes)
  setUser(user: User | null): void {
    const wasSignedIn = !!this.user;
    this.user = user;
    
    // If user just signed in and sync is enabled, trigger sync
    if (user && !wasSignedIn && this.config.syncOnConnect) {
      this.syncLocalToRemote();
    }
  }

  // Enable Supabase provider when ready
  setSupabaseProvider(provider: StorageProvider): void {
    console.log('🔌 Setting Supabase provider:', {
      wasEnabled: this.config.enableSupabase,
      hadProvider: !!this.supabaseProvider,
      newProvider: !!provider,
      userId: this.user?.id
    });
    
    this.supabaseProvider = provider;
    this.config.enableSupabase = true;
    
    console.log('✅ Supabase provider configured:', {
      enabled: this.config.enableSupabase,
      hasProvider: !!this.supabaseProvider,
      hasUser: !!this.user
    });
  }

  async getItem<T>(key: string): Promise<T | null> {
    // Special handling for sessions - merge data from both sources
    if (key === 'deletoSessions' && this.user && this.config.enableSupabase && this.supabaseProvider) {
      try {
        const [remoteData, localData] = await Promise.all([
          this.supabaseProvider.getItem<T>(key),
          this.localStorage.getItem<T>(key)
        ]);

        // Merge sessions from both sources
        const mergedData = this.mergeSessions(remoteData as any, localData as any);
        
        // Update both storages with merged data
        if (mergedData && mergedData.length > 0) {
          await Promise.all([
            this.localStorage.setItem(key, mergedData as T),
            this.supabaseProvider.setItem(key, mergedData as T)
          ]);
        }
        
        return mergedData as T;
      } catch (error) {
        console.warn('Error merging session data, falling back to localStorage:', error);
        return await this.localStorage.getItem<T>(key);
      }
    }

    // For non-session data, use original logic
    if (this.user && this.config.enableSupabase && this.supabaseProvider) {
      try {
        const result = await this.supabaseProvider.getItem<T>(key);
        if (result !== null) {
          // Update local cache
          if (this.config.enableLocalBackup) {
            await this.localStorage.setItem(key, result);
          }
          return result;
        }
      } catch (error) {
        console.warn('Supabase getItem failed, falling back to localStorage:', error);
      }
    }

    // Fallback to localStorage
    return await this.localStorage.getItem<T>(key);
  }

  async setItem<T>(key: string, value: T): Promise<boolean> {
    let localSuccess = true;
    let remoteSuccess = true;

    // Always save to localStorage for immediate access and backup
    if (this.config.enableLocalBackup || !this.user) {
      localSuccess = await this.localStorage.setItem(key, value);
    }

    // Debug logging for Supabase saving conditions
    if (key === 'deletoSessions') {
      console.log('🔍 Session save debug:', {
        hasUser: !!this.user,
        userId: this.user?.id,
        supabaseEnabled: this.config.enableSupabase,
        hasSupabaseProvider: !!this.supabaseProvider,
        sessionCount: Array.isArray(value) ? (value as any[]).length : 'not-array'
      });
    }

    // If user is signed in and Supabase is available, save to Supabase
    if (this.user && this.config.enableSupabase && this.supabaseProvider) {
      try {
        console.log(`💾 Saving ${key} to Supabase for user ${this.user.id}`);
        remoteSuccess = await this.supabaseProvider.setItem(key, value);
        if (remoteSuccess) {
          this.syncStatus.lastSync = new Date();
          console.log(`✅ Successfully saved ${key} to Supabase`);
        } else {
          console.warn(`❌ Failed to save ${key} to Supabase (returned false)`);
        }
      } catch (error) {
        console.warn('Supabase setItem failed, marked for later sync:', error);
        remoteSuccess = false;
        this.syncStatus.hasPendingChanges = true;
      }
    } else {
      if (key === 'deletoSessions') {
        console.log('⚠️ Not saving to Supabase because:', {
          reason: !this.user ? 'No user signed in' : 
                  !this.config.enableSupabase ? 'Supabase not enabled' :
                  !this.supabaseProvider ? 'No Supabase provider' : 'Unknown'
        });
      }
    }

    return localSuccess && (remoteSuccess || !this.user);
  }

  async removeItem(key: string): Promise<boolean> {
    let localSuccess = true;
    let remoteSuccess = true;

    // Remove from localStorage
    if (this.config.enableLocalBackup || !this.user) {
      localSuccess = await this.localStorage.removeItem(key);
    }

    // Remove from Supabase if available
    if (this.user && this.config.enableSupabase && this.supabaseProvider) {
      try {
        remoteSuccess = await this.supabaseProvider.removeItem(key);
      } catch (error) {
        console.warn('Supabase removeItem failed:', error);
        remoteSuccess = false;
      }
    }

    return localSuccess && (remoteSuccess || !this.user);
  }

  async clear(): Promise<boolean> {
    let localSuccess = true;
    let remoteSuccess = true;

    // Clear localStorage
    localSuccess = await this.localStorage.clear();

    // Clear Supabase if available
    if (this.user && this.config.enableSupabase && this.supabaseProvider) {
      try {
        remoteSuccess = await this.supabaseProvider.clear();
      } catch (error) {
        console.warn('Supabase clear failed:', error);
        remoteSuccess = false;
      }
    }

    return localSuccess && (remoteSuccess || !this.user);
  }

  // Sync methods for when Supabase is implemented
  async syncLocalToRemote(): Promise<boolean> {
    if (!this.user || !this.supabaseProvider || this.syncStatus.syncInProgress) {
      return false;
    }

    this.syncStatus.syncInProgress = true;
    
    try {
      // Get all GetDeleto data from localStorage
      const localData = await this.getLocalData();
      
      // Save to Supabase
      for (const [key, value] of Object.entries(localData)) {
        await this.supabaseProvider.setItem(key, value);
      }
      
      this.syncStatus.lastSync = new Date();
      this.syncStatus.hasPendingChanges = false;
      return true;
    } catch (error) {
      console.error('Sync to remote failed:', error);
      return false;
    } finally {
      this.syncStatus.syncInProgress = false;
    }
  }

  async syncRemoteToLocal(): Promise<boolean> {
    if (!this.user || !this.supabaseProvider) {
      console.log('🚫 Sync cancelled: Missing user or Supabase provider');
      return false;
    }
    
    if (this.syncStatus.syncInProgress) {
      console.log('⏸️ Sync already in progress, skipping');
      return false;
    }

    console.log('🔄 Starting sync from remote to local...');
    this.syncStatus.syncInProgress = true;
    
    // Add timeout to prevent hanging
    const syncTimeout = setTimeout(() => {
      console.error('⏰ Sync timeout after 30 seconds, resetting sync status');
      this.syncStatus.syncInProgress = false;
    }, 30000);
    
    try {
      console.log('📥 Fetching data from both sources...');
      
      // Get sessions from both sources and merge them
      const [remoteData, localData] = await Promise.all([
        this.supabaseProvider.getItem<SessionData[]>('deletoSessions'),
        this.localStorage.getItem<SessionData[]>('deletoSessions')
      ]);

      console.log('📊 Data fetched:', {
        remoteCount: remoteData?.length || 0,
        localCount: localData?.length || 0
      });

      const mergedSessions = this.mergeSessions(remoteData, localData);
      console.log(`🔧 Merged ${mergedSessions.length} unique sessions`);
      
      // Save merged data to both storages
      console.log('💾 Saving merged data to both storages...');
      await Promise.all([
        this.localStorage.setItem('deletoSessions', mergedSessions),
        this.supabaseProvider.setItem('deletoSessions', mergedSessions)
      ]);
      
      this.syncStatus.lastSync = new Date();
      this.syncStatus.hasPendingChanges = false;
      console.log(`✅ Successfully synced and merged ${mergedSessions.length} sessions between devices`);
      return true;
    } catch (error) {
      console.error('❌ Sync from remote failed:', error);
      return false;
    } finally {
      clearTimeout(syncTimeout);
      this.syncStatus.syncInProgress = false;
      console.log('🏁 Sync process completed, status reset');
    }
  }

  private async syncPendingChanges(): Promise<void> {
    if (this.syncStatus.hasPendingChanges && this.syncStatus.isOnline) {
      await this.syncLocalToRemote();
    }
  }

  // Merge sessions from remote and local sources, avoiding duplicates
  private mergeSessions(remoteData: SessionData[] | null, localData: SessionData[] | null): SessionData[] {
    const remote = remoteData || [];
    const local = localData || [];
    
    if (remote.length === 0) return local;
    if (local.length === 0) return remote;
    
    // Create a map to track sessions by ID to avoid duplicates
    const sessionMap = new Map<number, SessionData>();
    
    // Add remote sessions first
    remote.forEach(session => {
      sessionMap.set(session.id, session);
    });
    
    // Add local sessions, but only if they don't already exist (by ID)
    local.forEach(session => {
      if (!sessionMap.has(session.id)) {
        sessionMap.set(session.id, session);
      } else {
        // If session exists, prefer the one with more complete data or more recent date
        const existing = sessionMap.get(session.id)!;
        const existingDate = new Date(existing.date).getTime();
        const currentDate = new Date(session.date).getTime();
        
        // If local session is more recent or has more data, use it
        if (currentDate > existingDate || 
            (session.sessionDuration && !existing.sessionDuration) ||
            (session.deletedChars && !existing.deletedChars)) {
          sessionMap.set(session.id, session);
        }
      }
    });
    
    // Convert back to array and sort by date (newest first)
    return Array.from(sessionMap.values()).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  private async getLocalData(): Promise<Record<string, any>> {
    const data: Record<string, any> = {};
    
    // Get all GetDeleto specific keys
    const keys = [
      'deletoSessions',
      'inactivityTimeout', 
      'hideInactivitySeconds',
      'getdeleto-theme',
      'hasVisitedBefore'
    ];
    
    for (const key of keys) {
      const value = await this.localStorage.getItem(key);
      if (value !== null) {
        data[key] = value;
      }
    }
    
    return data;
  }

  // Getters for monitoring
  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  // Debug method to check current configuration
  getDebugInfo(): any {
    return {
      hasUser: !!this.user,
      userId: this.user?.id,
      userEmail: this.user?.email,
      supabaseEnabled: this.config.enableSupabase,
      hasSupabaseProvider: !!this.supabaseProvider,
      isOnline: this.syncStatus.isOnline,
      lastSync: this.syncStatus.lastSync,
      hasPendingChanges: this.syncStatus.hasPendingChanges,
      syncInProgress: this.syncStatus.syncInProgress
    };
  }

  // Debug method to manually reset sync status if stuck
  resetSyncStatus(): void {
    console.log('🔧 Manually resetting sync status');
    this.syncStatus.syncInProgress = false;
    this.syncStatus.hasPendingChanges = false;
  }

  // Debug method to force a new sync
  async forceSyncRemoteToLocal(): Promise<boolean> {
    console.log('🔄 Forcing sync (bypassing progress check)');
    
    if (!this.user || !this.supabaseProvider) {
      console.log('🚫 Force sync cancelled: Missing user or Supabase provider');
      return false;
    }

    // Reset sync status first
    this.syncStatus.syncInProgress = false;
    
    return await this.syncRemoteToLocal();
  }

  getConfig(): StorageConfig {
    return { ...this.config };
  }

  isUserSignedIn(): boolean {
    return !!this.user;
  }

  // Get storage info (from localStorage provider)
  getStorageInfo(): { used: string; available: string; percentage: number } {
    return this.localStorage.getStorageInfo();
  }
} 